import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimPriceLevelEnum } from 'gql/graphql';
import { totalsByConstructionTypeKey } from 'pages/Project/BimProject/WorkOrder/types';

import { useEffect, useMemo } from 'react';

import { bimWorkOrderTotalsRow } from '../../bimWorkOrderQueries';

export const useBimWorkOrderTotalsTable = ({
    bimTotalsChapter,
    workOrderId,
    constructionTypeId,
    priceLevel,
}: {
    bimTotalsChapter: string;
    workOrderId: number;
    constructionTypeId: number | undefined;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const isTotalsByConstructionType = useMemo(
        () => bimTotalsChapter === totalsByConstructionTypeKey,
        [bimTotalsChapter],
    );

    const totalVariables = useMemo(() => {
        if (isTotalsByConstructionType) {
            return {
                workOrderId,
                constructionTypeId,
                useInPrices: false,
                priceLevel,
            };
        }

        return {
            workOrderId,
            useInPrices: false,
            priceLevel,
        };
    }, [constructionTypeId, isTotalsByConstructionType, priceLevel, workOrderId]);

    const isRequestLegit = useMemo(() => {
        if (isTotalsByConstructionType) {
            return !!(workOrderId && constructionTypeId);
        }

        if (!isTotalsByConstructionType) {
            return !!workOrderId;
        }

        return false;
    }, [constructionTypeId, isTotalsByConstructionType, workOrderId]);

    const requestId = `bimWorkOrderTotalsTable${isTotalsByConstructionType ? '_ByConstructionType' : ''}`;

    const [makeReset] = useResetStoreData({
        requestId,
    });

    useEffect(
        () => () => {
            makeReset(undefined);
        },
        [makeReset, totalVariables],
    );

    const [rowTotalsData, rowTotalsDataStatus] = useRequest({
        requestId,
        query: bimWorkOrderTotalsRow,
        variables: totalVariables,
        withEffect: isRequestLegit,
    });

    const isLoading = rowTotalsDataStatus === RequestStatus.FETCHING;

    return {
        tableData: rowTotalsData?.bimWorkOrderTotals?.items,
        isLoading,
    };
};
